import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  fetchTodoTags,
  selectAllTodoTags,
  selectTodoTagsStatus,
} from 'store/slices/todoTagsSlice';
import {
  updateTodo,
  updateTodoTags,
  fetchTodos,
  selectTodosLoading,
  selectAllTodos,
  selectTodosStatus
} from 'store/slices/todosSlice';
import Page from 'components/layout/Page/Page';
import { toMarkdownString } from 'utils/markdown/markdown';
import TodoTabs from './components/TodoTabs/TodoTabs';
import TodoForm from './components/TodoForm/TodoForm';

export default function EditTodo() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const returnTo = location.state?.from || '/todos';

  const loading = useSelector(selectTodosLoading);
  const todos = useSelector(selectAllTodos);
  const status = useSelector(selectTodosStatus);
  const todoTags = useSelector(selectAllTodoTags);
  const todoTagsStatus = useSelector(selectTodoTagsStatus);
  const [saving, setSaving] = useState(false);

  const todo = useMemo(() =>
    todos.find(t => (t._id === id || t.id === id)),
    [todos, id]
  );

  useEffect(() => {
    if (!todo && status === 'idle') {
      dispatch(fetchTodos());
    }
  }, [todo, status, dispatch]);

  useEffect(() => {
    if (todoTagsStatus === 'idle') {
      dispatch(fetchTodoTags());
    }
  }, [dispatch, todoTagsStatus]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const {
        _id,
        tagNames = [],
        ...updateData
      } = data;
      delete updateData.tags;
      updateData.body = toMarkdownString(updateData.body);
      await dispatch(updateTodo({ id, data: updateData })).unwrap();
      await dispatch(updateTodoTags({ id, tagNames })).unwrap();
      navigate(returnTo);
    } catch (error) {
      console.error('Failed to update todo:', error);
    } finally {
      setSaving(false);
    }
  };

  const isFetching = !todo && loading;
  const isNotFound = !todo && !loading && status === 'succeeded';

  return (
    <Page title="Edit Todo" headerActions={<TodoTabs />}>
      <div className="max-w-2xl mx-auto">
        {isNotFound ? (
          <div className="text-center p-8 text-gray-500">
            Todo not found.
          </div>
        ) : (
          <TodoForm
            key={todo?._id || 'loading'}
            defaultValues={todo || {}}
            availableTagNames={todoTags.map((todoTag) => todoTag.name)}
            onSubmit={onSubmit}
            loading={isFetching}
            saving={saving}
            submitLabel="Update"
            cancelLink={returnTo}
          />
        )}
      </div>
    </Page>
  );
}
